import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import {
  Sparkles,
  ArrowRight,
  ChevronDown,
  Mic,
  Shield,
} from "lucide-react";
import { useEffect, useState } from "react";
import QuickstartCarousel from "@/components/quickstart-carousel";
import ShareLoopSection from "@/components/share-loop-section";
import GetCreativeSection from "@/components/get-creative-section";
import VoiceProfileSnapshot from "@/components/voice-profile-snapshot";
import { ScribPostLogo } from "@/components/scribpost-logo";
import { useAppStore } from "@/store";


const FAQS = [
  {
    q: "How does ScribPost learn my writing style?",
    a: "Paste 3–10 samples of your own writing. ScribPost's voice engine analyzes your vocabulary, tone, humor, and sentence patterns to build your personal voice profile.",
  },
  {
    q: "Will my content sound like AI?",
    a: "That's exactly what we built ScribPost to prevent. It generates content based on your specific voice profile — so everything sounds like you wrote it.",
  },
  {
    q: "What can ScribPost write for me?",
    a: "Anything in your voice: social posts (LinkedIn, Twitter/X, Instagram, Facebook, Reddit), email replies, comment responses, speeches, pitches, bios, and more.",
  },
  {
    q: "Can I speak my ideas instead of typing?",
    a: "Yes. Tap the mic, ramble your raw thoughts, and ScribPost transcribes and turns them into polished writing in your voice. No typing required.",
  },
  {
    q: "What platforms does ScribPost support?",
    a: "LinkedIn, Twitter/X, Instagram, Facebook, and Reddit — each formatted for that platform's style and audience expectations.",
  },
  {
    q: "How many free actions do I get?",
    a: "15 free actions, no credit card required. Each AI generation uses one action. Voice training is always free. After 15, ScribPost Pro is $8/month.",
  },
  {
    q: "Can I cancel my Pro subscription anytime?",
    a: "Yes. Cancel anytime, no lock-in. Your posts and voice profile are saved for 30 days after cancellation.",
  },
];

type StarItem =
  | { kind: "sparkle"; top?: string; bottom?: string; left?: string; right?: string; size: number; delay: string; duration: string }
  | { kind: "dot"; top?: string; bottom?: string; left?: string; right?: string; size: number; delay: string; duration: string };

const STARS: StarItem[] = [
  { kind: "sparkle", top: "13%",  left: "6%",   size: 20, delay: "0s",    duration: "5.5s" },
  { kind: "sparkle", top: "9%",   right: "8%",  size: 16, delay: "1.3s",  duration: "4.8s" },
  { kind: "sparkle", bottom: "22%", right: "9%", size: 22, delay: "0.6s",  duration: "6s"   },
  { kind: "sparkle", top: "48%",  left: "3%",   size: 14, delay: "2.1s",  duration: "5s"   },
  { kind: "sparkle", bottom: "30%", left: "11%", size: 18, delay: "1.7s",  duration: "4.5s" },
  { kind: "dot",     top: "7%",   left: "18%",  size: 5,  delay: "0.4s",  duration: "3.2s" },
  { kind: "dot",     top: "21%",  left: "2%",   size: 4,  delay: "1.1s",  duration: "4s"   },
  { kind: "dot",     top: "38%",  right: "6%",  size: 5,  delay: "0.8s",  duration: "3.6s" },
  { kind: "dot",     top: "5%",   right: "24%", size: 3,  delay: "1.9s",  duration: "5.2s" },
  { kind: "dot",     bottom: "28%", left: "5%", size: 4,  delay: "0.2s",  duration: "3.9s" },
  { kind: "dot",     bottom: "8%", left: "27%", size: 5,  delay: "1.5s",  duration: "4.4s" },
  { kind: "dot",     bottom: "18%", right: "3%",size: 4,  delay: "0.9s",  duration: "3.1s" },
  { kind: "dot",     top: "55%",  left: "9%",   size: 3,  delay: "2.4s",  duration: "4.7s" },
  { kind: "dot",     top: "28%",  right: "16%", size: 4,  delay: "0.5s",  duration: "3.5s" },
];

function StarField() {
  return (
    <>
      {STARS.map((s, i) => {
        const pos: React.CSSProperties = {
          position: "absolute",
          pointerEvents: "none",
          ...(s.top    ? { top: s.top }       : {}),
          ...(s.bottom ? { bottom: s.bottom } : {}),
          ...(s.left   ? { left: s.left }     : {}),
          ...(s.right  ? { right: s.right }   : {}),
        };

        if (s.kind === "sparkle") {
          return (
            <svg
              key={i}
              width={s.size}
              height={s.size}
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{
                ...pos,
                color: "hsl(188 90% 75%)",
                filter: `drop-shadow(0 0 ${Math.round(s.size * 0.5)}px hsl(188 90% 65% / 0.8))`,
                animation: `star-float ${s.duration} ease-in-out ${s.delay} infinite`,
              }}
            >
              <path d="M12 1 L13.8 10.2 L23 12 L13.8 13.8 L12 23 L10.2 13.8 L1 12 L10.2 10.2 Z" />
            </svg>
          );
        }

        return (
          <div
            key={i}
            style={{
              ...pos,
              width: s.size,
              height: s.size,
              borderRadius: "50%",
              background: "hsl(188 90% 80%)",
              boxShadow: `0 0 ${s.size * 2}px hsl(188 90% 65% / 0.9)`,
              animation: `star-twinkle ${s.duration} ease-in-out ${s.delay} infinite`,
            }}
          />
        );
      })}
    </>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-xl overflow-hidden transition-all duration-300 ${open ? "border-l-2 border-l-cyan-500/40 border-white/10" : "border-white/8"}`}
      style={{ background: open ? "hsl(222 44% 7% / 0.6)" : "hsl(222 44% 6% / 0.4)" }}>
      <button
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="font-semibold text-white text-sm">{q}</span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-all duration-300 ${open ? "text-cyan-400 drop-shadow-[0_0_6px_hsl(188_90%_42%_/_0.8)] rotate-180" : "text-slate-500"}`}
        />
      </button>
      <div
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-4 text-slate-400 text-sm leading-relaxed">{a}</div>
        </div>
      </div>
    </div>
  );
}

function FloatingCTA() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Link
      href="/start"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full font-bold text-sm text-white btn-shimmer shadow-lg transition-all duration-500"
      style={{
        background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))",
        boxShadow: "0 0 20px hsl(188 90% 42% / 0.5), 0 4px 24px rgba(0,0,0,0.4)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(16px) scale(0.95)",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      Start Free
      <ArrowRight className="w-4 h-4" />
    </Link>
  );
}

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "ScribPost",
  url: "https://scribpost.com",
  description: "AI post generator that sounds like you",
  potentialAction: {
    "@type": "SearchAction",
    target: "https://scribpost.com/blog?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

export default function LandingPage() {
  const { authToken } = useAppStore();

  return (
    <>
      <Helmet>
        <title>ScribPost — AI Post Generator That Sounds Like You</title>
        <meta
          name="description"
          content="Generate LinkedIn posts, tweets & more in your exact writing voice. ScribPost learns your style and writes content that's 100% you."
        />
        <link rel="canonical" href="https://scribpost.com/" />
        <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <FloatingCTA />

      <div className="min-h-screen bg-[#07091a] text-white overflow-x-hidden">

        {/* ── Nav ── */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#07091a]/85 backdrop-blur-xl nav-gradient-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <Link href="/" className="flex items-center">
              <ScribPostLogo size="md" />
            </Link>
            <div className="flex-1 hidden md:flex items-center gap-6 text-sm text-slate-300 ml-10">
              <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link href="/blog" className="hover:text-white transition-colors">Blog</Link>
              <Link href="/about" className="hover:text-white transition-colors">About</Link>
            </div>
            <div className="flex items-center gap-2">
              {authToken ? (
                <Link
                  href="/"
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity text-white btn-shimmer shadow-lg shadow-cyan-500/25"
                >
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    href="/login"
                    className="hidden sm:block px-4 py-2 rounded-xl text-sm font-medium border border-cyan-500/50 text-cyan-400 hover:border-cyan-400 hover:text-cyan-300 transition-all duration-200"
                  >
                    Log in
                  </Link>
                  <Link
                    href="/signup"
                    className="hidden sm:block px-4 py-2 rounded-xl text-sm font-medium border border-white/20 text-slate-200 hover:bg-white/5 hover:border-white/35 hover:text-white transition-all duration-200"
                  >
                    Create Account
                  </Link>
                  <Link
                    href="/start"
                    className="px-5 py-2.5 rounded-xl text-sm font-bold text-white btn-shimmer"
                    style={{
                      background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))",
                      boxShadow: "0 0 16px hsl(188 90% 42% / 0.45), 0 0 32px hsl(188 90% 42% / 0.15)",
                      animation: "btn-glow-pulse 2.4s ease-in-out infinite",
                    }}
                  >
                    Start Free
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* ── Hero ── */}
        <section className="relative pt-8 pb-20 px-4 sm:px-6 text-center overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 90% 60% at 50% -10%, hsl(188 90% 42% / 0.22) 0%, hsl(205 88% 52% / 0.08) 45%, transparent 70%)",
            }}
          />
          <div
            className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none hero-glow-pulse"
            style={{
              background:
                "radial-gradient(circle, hsl(188 90% 42% / 0.09) 0%, transparent 65%)",
              filter: "blur(60px)",
            }}
          />

          <StarField />

          <div className="relative max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-cyan-400 text-sm font-medium mb-6 shadow-[0_0_20px_hsl(188_90%_42%_/_0.15)]">
              <Sparkles className="w-4 h-4 shrink-0" />
              <span>Create with your voice,{" "}
                <span
                  className="text-amber-300 font-semibold"
                  style={{ textShadow: "0 0 12px hsl(45 95% 60% / 0.55)" }}
                >
                  at light speed
                </span>
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl font-black leading-[1.05] tracking-tight mb-5" style={{ fontFamily: "var(--font-display)" }}>
              The AI Writing Tool That{" "}
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-300 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(34,211,238,0.35)]">
                Sounds Exactly Like You
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-300 max-w-xl mx-auto mb-6 leading-relaxed">
              ScribPost trains on your writing, then generates anything you need to write — in your voice, for any format or occasion.
            </p>

            <div className="flex items-center justify-center gap-6 text-sm text-slate-400 mb-9">
              <span className="flex items-center gap-1.5">
                <Mic className="w-4 h-4 text-cyan-400" />
                Record audio or type your idea
              </span>
              <span className="w-px h-4 bg-white/10" />
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                Write anything — any format, any occasion
              </span>
              <span className="w-px h-4 bg-white/10 hidden sm:block" />
              <span className="hidden sm:flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-cyan-400" />
                Saves you hours every week
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Link
                href="/start"
                className="w-full sm:w-auto px-9 py-4 rounded-2xl text-base font-bold text-white flex items-center justify-center gap-2 btn-shimmer btn-glow-pulse"
                style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
              >
                Start Free — No Card Needed
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <p className="text-sm text-slate-500">15 free actions · No credit card · Cancel anytime</p>
            <p className="text-sm text-slate-500 mt-2">
              Already have an account?{" "}
              <Link href="/login" className="text-cyan-400/80 hover:text-cyan-300 transition-colors font-medium">
                Log in →
              </Link>
            </p>
          </div>
        </section>

        {/* ── Quickstart Carousel ── */}
        <QuickstartCarousel />

        {/* ── Section 1: Share Loop ── */}
        <div className="section-divider" />
        <ShareLoopSection />

        {/* ── Section 2: Get Creative ── */}
        <div className="section-divider" />
        <GetCreativeSection />

        {/* ── Section 3: Voice Profile Snapshot ── */}
        <div className="section-divider" />
        <VoiceProfileSnapshot />

        {/* ── Pricing teaser ── */}
        <div className="section-divider" />
        <section className="py-16 px-4 sm:px-6" style={{ background: "linear-gradient(180deg, #07091a 0%, #0a0d22 100%)" }}>
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-xs text-cyan-400 font-semibold uppercase tracking-widest mb-4" style={{ filter: "drop-shadow(0 0 6px hsl(188 90% 42% / 0.55))" }}>Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-5">Free to start. $8/mo to go unlimited.</h2>
            <p className="text-slate-300 mb-8 leading-relaxed">
              15 free actions — no credit card, full platform access. ScribPost Pro is $8/month for unlimited posts, emails, replies, and speeches in your voice.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Link
                href="/start"
                className="px-8 py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-2 btn-shimmer btn-glow-pulse"
                style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
              >
                Start Free
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/pricing"
                className="px-8 py-4 rounded-2xl font-semibold border border-white/20 hover:bg-white/5 transition-colors text-white"
              >
                See full pricing
              </Link>
            </div>
            <p className="text-sm text-slate-500">15 free actions · No card required · Cancel anytime</p>
          </div>
        </section>

        {/* ── FAQ ── */}
        <div className="section-divider" />
        <section className="py-14 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-xs text-cyan-400 font-semibold uppercase tracking-widest mb-3" style={{ filter: "drop-shadow(0 0 6px hsl(188 90% 42% / 0.55))" }}>FAQ</p>
              <h2 className="text-2xl font-bold mb-2">Common Questions</h2>
              <p className="text-slate-400 text-sm">Everything you need to know.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-2.5">
              {FAQS.map(({ q, a }) => (
                <FAQItem key={q} q={q} a={a} />
              ))}
            </div>
          </div>
        </section>

        {/* ── Final CTA ── */}
        <section className="py-24 px-4 sm:px-6 text-center relative overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 80% 50% at 50% 100%, hsl(188 90% 42% / 0.12) 0%, transparent 65%)",
            }}
          />
          <div className="relative max-w-2xl mx-auto">
            <p className="text-xs text-cyan-400 font-semibold uppercase tracking-widest mb-4" style={{ filter: "drop-shadow(0 0 6px hsl(188 90% 42% / 0.55))" }}>Get Started</p>
            <h2 className="text-4xl sm:text-5xl font-black mb-5 leading-tight" style={{ fontFamily: "var(--font-display)" }}>
              Ready to Sound Like{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Yourself Online?
              </span>
            </h2>
            <p className="text-slate-300 mb-10 leading-relaxed text-lg">
              Train your voice. Write your first post in under 5 minutes.
            </p>
            <Link
              href="/start"
              className="inline-flex items-center gap-2 px-12 py-5 rounded-2xl font-bold text-white text-lg btn-shimmer btn-glow-pulse"
              style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
            >
              Get Started Free
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="text-sm text-slate-500 mt-5">No credit card · 15 free actions · Cancel anytime</p>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="border-t border-white/[0.06] py-10 px-4 sm:px-6 bg-[#06081a]">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <Link href="/" className="flex items-center gap-1.5 group">
                <span className="font-bold text-xl tracking-tight text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.5)] group-hover:drop-shadow-[0_0_16px_rgba(34,211,238,0.7)] transition-all">
                  Scrib
                </span>
                <span className="font-bold text-xl tracking-tight text-white">Post</span>
              </Link>
              <nav className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
                <Link href="/about" className="hover:text-white transition-colors">About</Link>
                <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link href="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
              </nav>
              <p className="text-xs text-slate-600">© 2026 ScribPost. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
